import { Box, styled } from '@mui/material';
import { ReactNode, useId } from 'react';

/**
 * Define SVG mask shapes.
 * maskContentUnits="objectBoundingBox" allows using coordinates from 0 to 1.
 */
const MASK_SHAPES = {
  circle: <circle cx="0.5" cy="0.5" r="0.5" fill="white" />,
  squircle: (
    <path
      d="M 0,0.5 C 0,0.05 0.05,0 0.5,0 C 0.95,0 1,0.05 1,0.5 C 1,0.95 0.95,1 0.5,1 C 0.05,1 0,0.95 0,0.5"
      fill="white"
    />
  ),
  arch: (
    <path
      d="M 0,1 L 0,0.5 C 0,0.22 0.22,0 0.5,0 C 0.78,0 1,0.22 1,0.5 L 1,1 Z"
      fill="white"
    />
  ),
  diamond: <path d="M 0.5,0 L 1,0.5 L 0.5,1 L 0,0.5 Z" fill="white" />,
  hexagon: (
    <path
      d="M 0.5,0 L 0.933,0.25 V 0.75 L 0.5,1 L 0.067,0.75 V 0.25 Z"
      fill="white"
    />
  ),
  triangle: <path d="M 0.5,0.05 L 1,0.95 H 0 Z" fill="white" />,
  leaf: <path d="M 0,1 C 0,0.45 0.45,0 1,0 V 1 Z" fill="white" />,
  pill: <rect x="0" y="0.2" width="1" height="0.6" rx="0.3" fill="white" />,
  star: (
    <path
      d="M 0.5,0.05 L 0.61,0.38 H 0.95 L 0.68,0.58 L 0.79,0.91 L 0.5,0.71 L 0.21,0.91 L 0.32,0.58 L 0.05,0.38 H 0.39 Z"
      fill="white"
    />
  ),
  blob1: (
    <path
      d="M 0.9,0.4 C 0.95,0.6 0.8,0.85 0.6,0.9 C 0.4,0.95 0.1,0.9 0.05,0.7 C 0,0.5 0.1,0.2 0.3,0.1 C 0.5,0 0.85,0.2 0.9,0.4 Z"
      fill="white"
    />
  ),
  blob2: (
    <path
      d="M 0.85,0.15 C 1,0.35 1,0.65 0.85,0.85 C 0.7,1.05 0.3,1.05 0.15,0.85 C 0,0.65 0,0.35 0.15,0.15 C 0.3,-0.05 0.7,-0.05 0.85,0.15 Z"
      fill="white"
    />
  ),
  wavy: (
    <path
      d="M 0,0.2 Q 0.25,0 0.5,0.2 T 1,0.2 V 0.8 Q 0.75,1 0.5,0.8 T 0,0.8 Z"
      fill="white"
    />
  ),
  '3hopsVertical': (
    <path
      fill="white"
      d="M0.799,0 A0.201,0.302,0,0,0,0.649,0.1 A0.201,0.303,0,0,0,0.351,0.1 A0.201,0.303,0,0,0,0,0.303 V0.697 A0.201,0.303,0,0,0,0.351,0.9 A0.201,0.303,0,0,0,0.649,0.9 A0.201,0.303,0,0,0,1,0.697 V0.303 A0.201,0.303,0,0,0,0.799,0"
    />
  ),
};

const MaskContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'maskId',
})<{ maskId: string }>(({ maskId }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  maskImage: `url(#${maskId})`,
  WebkitMaskImage: `url(#${maskId})`,
  maskSize: 'cover',
  WebkitMaskSize: 'cover',
}));

export type MaskVariant = keyof typeof MASK_SHAPES;
interface MaskProps {
  variant: MaskVariant;
  children?: ReactNode;
}

export const Mask = ({ variant, children }: MaskProps) => {
  const id = useId().replace(/:/g, '');
  const shape = MASK_SHAPES[variant] || MASK_SHAPES.circle;

  return (
    <>
      <svg
        width="0"
        height="0"
        style={{ position: 'absolute', pointerEvents: 'none' }}
      >
        <defs>
          <mask
            id={id}
            maskUnits="objectBoundingBox"
            maskContentUnits="objectBoundingBox"
          >
            {shape}
          </mask>
        </defs>
      </svg>
      <MaskContainer maskId={id}>{children}</MaskContainer>
    </>
  );
};
